import { t } from "@lingui/macro";
import { FlashList } from "@shopify/flash-list";
import graphql from "babel-plugin-relay/macro";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import {
  Box,
  HStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { EventDetailAttendeesGroupListItem } from "./EventDetailAttendeesGroupListItem";
import { EventDetailAttendeesListItem } from "./EventDetailAttendeesListItem";
import { EventDetailAttendeesGroupListItemFragment$key } from "./__generated__/EventDetailAttendeesGroupListItemFragment.graphql";
import { EventDetailAttendeesList$key } from "./__generated__/EventDetailAttendeesList.graphql";
import { EventDetailAttendeesListItemFragment$key } from "./__generated__/EventDetailAttendeesListItemFragment.graphql";

type EventDetailAttendeesListProps = {
  fragmentRef: EventDetailAttendeesList$key;
};

export const EventDetailAttendeesList = ({
  fragmentRef,
}: EventDetailAttendeesListProps) => {
  const data = useFragment(EventDetailAttendeesListFragment, fragmentRef);

  const isGroupEvent =
    data?.maxGuestCount !== 0 ||
    data?.maxMemberCount !== 1 ||
    data?.minMemberCount !== 1 ||
    data?.minGuestCount !== 0;

  const isGroupCapacity = data.capacityType === "GROUPS";

  const { accessStatusForClient } = data;
  const eventPlayerNoun = getNounOverride(
    data.eventSeries?.facility?.copyOverrides?.eventPlayer?.singular,
    t`Player`
  );

  const numAttendees =
    isGroupEvent && isGroupCapacity
      ? data?.eventSignUps?.edges?.length
      : data?.eventSignUps?.edges?.reduce(
          (a, b) => a + (b?.node?.registrations?.count ?? 0),
          0
        );

  const eventSignUpRows: {
    groupFragmentRef: EventDetailAttendeesGroupListItemFragment$key | null;
    fragmentRef: EventDetailAttendeesListItemFragment$key | null;
    id?: string;
  }[] =
    data.eventSignUps?.edges?.map((item) => ({
      id: item?.node?.id,
      fragmentRef: item?.node ?? null,
      groupFragmentRef: item?.node ?? null,
    })) || [];

  const subheadingMessage = [
    "SIGNUP_AVAILABLE",
    "PENDING",
    "WAITLIST_AVAILABLE",
  ].includes(accessStatusForClient ?? "")
    ? data.spotsAvailable !== null
      ? `${data.spotsAvailable || 0} ${
          data.spotsAvailable === 1 ? t`Spot Left` : t`Spots Left`
        }`
      : null
    : null;

  if (numAttendees === 0) return null;

  return (
    <Box pt={2}>
      <HStack justifyContent="space-between" py={2}>
        <Text variant="mainSmallSemiBold">
          {!isGroupEvent ? t`Attendees` : null}
          {isGroupEvent
            ? `${
                isGroupCapacity ? t`Groups ` : t`Attendees `
              } (${numAttendees}${data?.capacity ? `/${data.capacity})` : ")"}`
            : null}
        </Text>
        <Text color="text-main-primary" variant="mainSmallSemiBold">
          {subheadingMessage}
        </Text>
      </HStack>

      <Box pt={2} flex={1} mt={1}>
        <FlashList
          data={eventSignUpRows}
          showsVerticalScrollIndicator={Platform.OS === "web"}
          keyExtractor={(item) => `attendee-item-${item?.id}`}
          renderItem={({ item }) => {
            if (isGroupEvent) {
              return (
                <EventDetailAttendeesGroupListItem
                  fragmentRef={item.groupFragmentRef}
                  playerNounOverride={eventPlayerNoun}
                />
              );
            } else {
              return (
                <EventDetailAttendeesListItem
                  fragmentRef={item.fragmentRef}
                  playerNounOverride={eventPlayerNoun}
                />
              );
            }
          }}
          ListFooterComponent={<Box mb={{ base: 5, md: 32 }} />}
        />
      </Box>
    </Box>
  );
};

export const EventDetailAttendeesListFragment = graphql`
  fragment EventDetailAttendeesList on Event {
    id
    accessStatusForClient(clientId: $memberId)
    signUpStatusForClient(clientId: $memberId)
    spotsAvailable
    capacityType
    maxGuestCount
    maxMemberCount
    minMemberCount
    minGuestCount
    capacity
    hasWaitlist
    eventSeries {
      facility {
        copyOverrides {
          eventPlayer {
            singular
          }
        }
      }
    }
    eventSignUps: signUps(waitlist: false) {
      edges {
        node {
          id
          registrations {
            count
          }
          ...EventDetailAttendeesListItemFragment
          ...EventDetailAttendeesGroupListItemFragment
        }
      }
    }
  }
`;
